import { FactorEntry } from "../services/productFactorsService";

export interface RiskMetrics {
  volatility: number | null;
  downsideDailyMove: number | null;
  maxDrawdown: number | null;
  sharpe: number | null;
  sortino: number | null;
  beta: number | null;
  upCapture: number | null;
  downCapture: number | null;
  upParticipation: number | null;
  trackingError: number | null;
  informationRatio: number | null;
  bestDay: number | null;
  worstDay: number | null;
  cumulativeReturn: number | null;
}

export interface CorrelationResult {
  products: string[];
  matrix: (number | null)[][];
}

const ANNUALIZATION_FACTOR = Math.sqrt(252);
const MIN_DATA_POINTS = 10;

/**
 * Convert raw factor entries to one compounded return per UTC day.
 * API factor rows are multiplicative period values and may be intraday.
 */
export function factorsToDailyReturns(factors: FactorEntry[]): { t: string; r: number }[] {
  const dailyFactors = new Map<string, number>();

  const sorted = [...factors].sort((a, b) => parseTimeMs(a.t) - parseTimeMs(b.t));
  for (const factor of sorted) {
    const numericFactor = Number(factor.f);
    if (!Number.isFinite(numericFactor)) continue;
    const dayKey = toUtcDateKey(factor.t);
    dailyFactors.set(dayKey, (dailyFactors.get(dayKey) ?? 1) * numericFactor);
  }

  return Array.from(dailyFactors.entries()).map(([dayKey, factor]) => ({
    t: `${dayKey}T00:00:00.000Z`,
    r: factor - 1,
  }));
}

export function factorsToReturns(factors: FactorEntry[]): { t: string; r: number }[] {
  return factorsToDailyReturns(factors);
}

/**
 * Align two return series by shared UTC dates.
 * Product and benchmark factors can be recorded at different intraday timestamps.
 */
export function alignReturnSeries(
  seriesA: { t: string; r: number }[],
  seriesB: { t: string; r: number }[]
): { a: number[]; b: number[] } {
  const mapB = new Map(seriesB.map((s) => [toUtcDateKey(s.t), s.r]));
  const a: number[] = [];
  const b: number[] = [];
  for (const point of seriesA) {
    const bVal = mapB.get(toUtcDateKey(point.t));
    if (bVal !== undefined) {
      a.push(point.r);
      b.push(bVal);
    }
  }
  return { a, b };
}

function toUtcDateKey(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toISOString().slice(0, 10);
}

function parseTimeMs(value: string): number {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function mean(arr: number[]): number {
  return arr.reduce((sum, v) => sum + v, 0) / arr.length;
}

function stdDev(arr: number[], avg?: number): number {
  const m = avg ?? mean(arr);
  const variance = arr.reduce((sum, v) => sum + (v - m) ** 2, 0) / (arr.length - 1);
  return Math.sqrt(variance);
}

function downsideDeviation(returns: number[], threshold: number = 0): number {
  const downside = returns.filter((r) => r < threshold);
  if (downside.length < 2) return 0;
  const variance = downside.reduce((sum, r) => sum + (r - threshold) ** 2, 0) / (downside.length - 1);
  return Math.sqrt(variance);
}

/**
 * Compute cumulative return from daily returns.
 */
function cumulativeReturn(returns: number[]): number {
  return returns.reduce((cum, r) => cum * (1 + r), 1) - 1;
}

function annualizedReturn(returns: number[]): number {
  return (1 + cumulativeReturn(returns)) ** (252 / returns.length) - 1;
}

function normalizeZero(value: number): number {
  return Math.abs(value) < 1e-12 ? 0 : value;
}

/**
 * Compute max drawdown from daily returns.
 */
function maxDrawdown(returns: number[]): number {
  let peak = 1;
  let maxDD = 0;
  let cumulative = 1;
  for (const r of returns) {
    cumulative *= 1 + r;
    if (cumulative > peak) peak = cumulative;
    const dd = (cumulative - peak) / peak;
    if (dd < maxDD) maxDD = dd;
  }
  return maxDD;
}

/**
 * Compute full risk metrics for a series against a benchmark.
 */
export function computeRiskMetrics(
  factors: FactorEntry[],
  benchmarkFactors: FactorEntry[],
  riskFreeRate: number = 0.0525
): RiskMetrics {
  const returns = factorsToReturns(factors);
  const bmkReturns = factorsToReturns(benchmarkFactors);

  if (returns.length < MIN_DATA_POINTS) {
    return {
      volatility: null, downsideDailyMove: null, maxDrawdown: null, sharpe: null, sortino: null,
      beta: null, upCapture: null, downCapture: null, upParticipation: null,
      trackingError: null, informationRatio: null, bestDay: null, worstDay: null,
      cumulativeReturn: null,
    };
  }

  const { a: alignedStrategy, b: alignedBenchmark } = alignReturnSeries(returns, bmkReturns);

  const dailyReturns = returns.map((r) => r.r);
  const dailyRf = riskFreeRate / 252;

  const vol = stdDev(dailyReturns) * ANNUALIZATION_FACTOR;
  const mdd = maxDrawdown(dailyReturns);
  const cumRet = cumulativeReturn(dailyReturns);

  const strategyAnnualizedReturn = annualizedReturn(dailyReturns);
  const sharpe = vol > 0 ? (strategyAnnualizedReturn - riskFreeRate) / vol : null;

  const dd = downsideDeviation(dailyReturns, dailyRf) * ANNUALIZATION_FACTOR;
  const sortino = dd > 0 ? (strategyAnnualizedReturn - riskFreeRate) / dd : null;

  // Typical down-day: dispersion of losing days only, measured around 0 (not the
  // mean), so it is a distinct statistic from the headline daily move -- it can even
  // exceed it for loss-dominated series. Drives the daily-swings rating as a
  // downside / benchmark-downside ratio (upside-insensitive). Per-day, not
  // annualized, to match the other daily figures.
  // A single losing day has no dispersion to compute, so use its magnitude directly
  // rather than letting downsideDeviation() collapse it to 0 (which would mis-rate a
  // one-loss window as Good). Zero only when there were genuinely no losing days.
  const losingDays = dailyReturns.filter((r) => r < 0);
  const downsideDailyMove =
    losingDays.length >= 2
      ? downsideDeviation(dailyReturns, 0) * 100
      : losingDays.length === 1
        ? Math.abs(losingDays[0]) * 100
        : 0;

  let beta: number | null = null;
  let upCapture: number | null = null;
  let downCapture: number | null = null;
  let upParticipation: number | null = null;
  let trackingError: number | null = null;
  let informationRatio: number | null = null;

  if (alignedStrategy.length >= MIN_DATA_POINTS) {
    const bmkMean = mean(alignedBenchmark);
    const bmkVar = alignedBenchmark.reduce((sum, v) => sum + (v - bmkMean) ** 2, 0) / (alignedBenchmark.length - 1);
    const stratMean = mean(alignedStrategy);
    const covariance = alignedStrategy.reduce((sum, v, i) =>
      sum + (v - stratMean) * (alignedBenchmark[i] - bmkMean), 0
    ) / (alignedStrategy.length - 1);
    beta = bmkVar > 0 ? covariance / bmkVar : null;

    const upDays = alignedBenchmark
      .map((b, i) => ({ b, s: alignedStrategy[i] }))
      .filter((d) => d.b > 0);
    const downDays = alignedBenchmark
      .map((b, i) => ({ b, s: alignedStrategy[i] }))
      .filter((d) => d.b < 0);

    if (upDays.length > 0) {
      const avgStratUp = mean(upDays.map((d) => d.s));
      const avgBmkUp = mean(upDays.map((d) => d.b));
      upCapture = avgBmkUp !== 0 ? (avgStratUp / avgBmkUp) * 100 : null;
      upParticipation = (upDays.filter((d) => d.s > 0).length / upDays.length) * 100;
    }
    if (downDays.length > 0) {
      const avgStratDown = mean(downDays.map((d) => d.s));
      const avgBmkDown = mean(downDays.map((d) => d.b));
      downCapture = avgBmkDown !== 0 ? (avgStratDown / avgBmkDown) * 100 : null;
    }

    const activeReturns = alignedStrategy.map((value, index) => value - alignedBenchmark[index]);
    const annualizedTrackingError = normalizeZero(stdDev(activeReturns) * ANNUALIZATION_FACTOR);
    trackingError = annualizedTrackingError * 100;
    if (annualizedTrackingError > 0) {
      const activeAnnualizedReturn =
        annualizedReturn(alignedStrategy) - annualizedReturn(alignedBenchmark);
      informationRatio = activeAnnualizedReturn / annualizedTrackingError;
    }
  }

  const bestDay = Math.max(...dailyReturns);
  const worstDay = Math.min(...dailyReturns);

  return {
    volatility: vol * 100,
    downsideDailyMove,
    maxDrawdown: mdd * 100,
    sharpe,
    sortino,
    beta,
    upCapture,
    downCapture,
    upParticipation,
    trackingError,
    informationRatio,
    bestDay: bestDay * 100,
    worstDay: worstDay * 100,
    cumulativeReturn: cumRet * 100,
  };
}

/**
 * Compute Pearson correlation matrix between product factor series.
 */
export function computeCorrelationMatrix(
  productFactors: Map<string, FactorEntry[]>
): CorrelationResult {
  const keys = Array.from(productFactors.keys());
  const returnSeries = new Map<string, { t: string; r: number }[]>();

  for (const key of keys) {
    returnSeries.set(key, factorsToReturns(productFactors.get(key)!));
  }

  const matrix: (number | null)[][] = keys.map(() => keys.map(() => null));

  for (let i = 0; i < keys.length; i++) {
    matrix[i][i] = 1;
    for (let j = i + 1; j < keys.length; j++) {
      const seriesA = returnSeries.get(keys[i])!;
      const seriesB = returnSeries.get(keys[j])!;
      const { a, b } = alignReturnSeries(seriesA, seriesB);

      if (a.length < MIN_DATA_POINTS) {
        matrix[i][j] = null;
        matrix[j][i] = null;
        continue;
      }

      const meanA = mean(a);
      const meanB = mean(b);
      const stdA = stdDev(a, meanA);
      const stdB = stdDev(b, meanB);

      if (stdA === 0 || stdB === 0) {
        matrix[i][j] = null;
        matrix[j][i] = null;
        continue;
      }

      const cov = a.reduce((sum, v, idx) => sum + (v - meanA) * (b[idx] - meanB), 0) / (a.length - 1);
      const corr = cov / (stdA * stdB);
      matrix[i][j] = corr;
      matrix[j][i] = corr;
    }
  }

  return { products: keys, matrix };
}

/**
 * Compute strategy exposure from flat position weights.
 */
export function computeStrategyExposure(
  positionWeights: { netWeight: number }[]
): { gross: number; net: number; long: number; short: number; cash: number } {
  let long = 0;
  let short = 0;
  for (const p of positionWeights) {
    if (p.netWeight > 0) long += p.netWeight;
    else short += Math.abs(p.netWeight);
  }
  return {
    gross: long + short,
    net: long - short,
    long,
    short,
    cash: 100 - (long - short),
  };
}
